<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { RouterView, useRouter } from 'vue-router'
import { useClarity } from '@/composables/useClarity.ts'
import jwtService from '@/services/jwtService.ts'
import emitter from '@/services/emitter.ts'
import { jwtDecode } from 'jwt-decode'
import userAuthorizationService from '@/services/userAuthorizationService.ts'
import userService from '@/services/userService.ts'
import AppLayout from '@/layouts/AppLayout.vue'
import { Roles } from './constants'
import type { JwtPayload } from '@/types/auth'

const router = useRouter()
const isAuthorized = ref(false)
const currentUserRole = ref<Roles | null>(null)

const showLayout = () => router.currentRoute.value.path !== '/login' && isAuthorized.value

router.beforeEach((to, from, next) => {
  if (to.path === '/login') {
    next()
    return
  }

  const token = jwtService.getToken()
  if (!token) {
    router.push('/login')
    return
  }

  isAuthorized.value = true

  const decoded = jwtDecode<JwtPayload>(token)
  const currentRole = decoded.role as Roles
  const allowedRoles = to.meta.allowedRoles as Roles[]

  if (allowedRoles.includes(currentRole)) {
    currentUserRole.value = currentRole
    next()
  } else {
    logout()
    isAuthorized.value = false
  }
})

const clearStorage = () => {
  localStorage.clear()
  location.reload()
}

const logout = async () => {
  await userAuthorizationService.logout()
}

emitter.on('logoutRequired', () => {
  jwtService.clear()
  clearStorage()
  router.push('/login')
})

onMounted(async () => {
  try {
    const clarity = await useClarity()
    const userIdForIntegrations = userService.getUserIdForIntegrations()
    if (userIdForIntegrations != null) {
      clarity.identify(userIdForIntegrations)
    }
  } catch {
    // Clarity не инициализирована (dev-окружение)
  }
})
</script>

<template>
  <AppLayout v-if="showLayout()" :role="currentUserRole" :on-logout="logout" />
  <RouterView v-else />
  <Toast />
</template>
